import React, { FC, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { SurfaceCard, PillButton } from "../ui";
import GuideLink from "../guides/GuideLink";
import { useUserProducts } from "../../hooks/use-user-products";
import { paths } from "../../routes/paths";

const PREVIEW_COUNT = 3;
const LIST_ID = "device-summary-list";

/**
 * "Your devices" summary.
 *
 * Starts collapsed, showing only the total, so the home page leads with
 * registration rather than with a list most installers do not need on arrival.
 *
 * A read failure degrades to a warning rather than blocking the page —
 * registering a product must never depend on this query succeeding.
 */
const DeviceSummaryCard: FC = () => {
  const navigate = useNavigate();
  const { products, loading, error, reload } = useUserProducts();
  const [expanded, setExpanded] = useState(false);

  if (loading) {
    return (
      <SurfaceCard>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="rectangular" height={72} sx={{ mt: 2, borderRadius: 1 }} />
      </SurfaceCard>
    );
  }

  if (error) {
    return (
      <SurfaceCard>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          Could not load your registered devices.
        </Alert>
      </SurfaceCard>
    );
  }

  if (products.length === 0) {
    return (
      <SurfaceCard>
        <Typography variant="h4" sx={{ mb: 1 }}>
          No devices yet
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Devices you register will appear here.
        </Typography>
        <PillButton
          tone="amber"
          size="small"
          withArrow
          sx={{ alignSelf: "flex-start" }}
          onClick={() => navigate(paths.registerSerial)}
        >
          Register your first product
        </PillButton>
      </SurfaceCard>
    );
  }

  const preview = products.slice(0, PREVIEW_COUNT);
  const total = `${products.length} ${
    products.length === 1 ? "device" : "devices"
  }`;

  return (
    <SurfaceCard>
      {/*
        The toggle and "View all" are siblings, not nested: putting a Button
        inside the toggle would be invalid HTML and unreachable by keyboard.
      */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <ButtonBase
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={LIST_ID}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            borderRadius: 1,
            px: 0.5,
            ml: -0.5,
            textAlign: "left",
          }}
        >
          <Typography variant="h4">{total}</Typography>
          <ExpandMoreIcon
            sx={{
              transition: "transform 0.2s",
              transform: expanded ? "rotate(180deg)" : "none",
              color: "text.secondary",
            }}
          />
        </ButtonBase>

        <Button size="small" onClick={() => navigate(paths.accountDevices)}>
          View all
        </Button>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box id={LIST_ID} sx={{ mt: 2 }}>
          {preview.map((product, index) => (
            <Box key={product.artome_serial_no}>
              {index > 0 && <Divider sx={{ my: 1.5 }} />}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  cursor: "pointer",
                }}
                onClick={() => navigate(paths.device(product.artome_serial_no))}
              >
                <Box>
                  <Typography variant="subtitle2">
                    {product.artome_product}
                  </Typography>
                  <Typography variant="caption">
                    {product.artome_serial_no}
                    {product.setup_date ? ` · ${product.setup_date}` : ""}
                  </Typography>
                </Box>
                <GuideLink product={product.artome_product} />
              </Box>
            </Box>
          ))}

          {products.length > PREVIEW_COUNT && (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5 }}>
              Showing {PREVIEW_COUNT} of {products.length}.
            </Typography>
          )}
        </Box>
      </Collapse>
    </SurfaceCard>
  );
};

export default DeviceSummaryCard;
